import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Card, Segment, Header, Grid, Pagination,
} from 'semantic-ui-react';
import { PROJECTS } from '../../queries/project';
import Loading from '../Loading';
import CreateProjectButton from './CreateProjectButton';
import ProjectFilters from './ProjectFilters';
import ProjectCard from './ProjectCard';
import DataContext from '../../contexts/DataContext';

const PROJECTS_PER_PAGE = 10;

const STATUS_OPTIONS = {
  active: ['Active', 'Draft'],
  completed: ['Completed'],
  all: ['Active', 'Draft', 'Completed'],
};

const statusKeyFromStatuses = (statuses) => {
  if (statuses.length === 1 && statuses[0] === 'Completed') return 'completed';
  if (statuses.length === 3) return 'all';
  return 'active';
};

const Projects = () => {
  const { user, team } = useContext(DataContext);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const view = params.get('view') === 'all' ? 'all' : 'mine';
  const statusParam = params.get('status');
  const statusKey = STATUS_OPTIONS[statusParam] ? statusParam : 'active';
  const page = params.get('page') ? parseInt(params.get('page'), 10) : 1;

  const updateParams = (updates) => {
    const newParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => newParams.set(key, value));
    history.push({ pathname: '/projects', search: `?${newParams.toString()}` });
  };

  const appliedFilters = {
    status: STATUS_OPTIONS[statusKey],
    service_type: 'Self-Service',
    myProjectsOnly: view === 'mine',
    team: team.id,
  };

  const { data, loading } = useQuery(PROJECTS, {
    variables: {
      query: '',
      filters: appliedFilters,
      pagination: { page, per_page: PROJECTS_PER_PAGE },
    },
    // Prevent any cache that affects data updating
    fetchPolicy: 'network-only',
  });

  if (loading) return <Loading />;

  const renderProjects = () => (
    <div>
      {data.projects.total === 0 ? (
        <Grid container centered>
          <Segment>
            <div className="not-found">
              <Header as="h2">No Projects Found</Header>
              <Header>
                Set up a project and start inviting experts best fitted to answer your questions
              </Header>
              <CreateProjectButton />
            </div>
          </Segment>
        </Grid>
      ) : (
        <div className="project-list">
          <Card.Group itemsPerRow={2} stackable>
            {data.projects.projects.map(
              (project) => (
                <ProjectCard
                  key={project.id}
                  user={user}
                  project={project}
                />
              ),
            )}
          </Card.Group>
          <div className="pagination">
            <Pagination
              activePage={page}
              totalPages={Math.ceil(data.projects.total / PROJECTS_PER_PAGE)}
              onPageChange={(e, d) => updateParams({ page: d.activePage })}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Grid.Row>
      <Grid.Column width={4}>
        <ProjectFilters
          filters={appliedFilters}
          updateFilter={(newData) => {
            const updates = { page: 1 };
            if ('myProjectsOnly' in newData) {
              updates.view = newData.myProjectsOnly ? 'mine' : 'all';
            }
            if ('status' in newData) {
              updates.status = statusKeyFromStatuses(newData.status);
            }
            updateParams(updates);
          }}
        />
      </Grid.Column>
      <Grid.Column width={12}>
        {renderProjects()}
      </Grid.Column>
    </Grid.Row>
  );
};

export default Projects;
